import {
  Paper,
  IconButton,
  Button,
  Tooltip,
  Select,
  MenuItem,
  TextField
} from "@mui/material";
import { ReactComponent as LineSvg } from "assets/line.svg";
import { ReactComponent as PolygonSvg } from "assets/polygon.svg";
import styles from "./shape-info.module.scss";
import { useTranslation } from "react-i18next";
import { useContext, useState, useEffect, useRef } from "react";
import { MapContext } from "helpers/map";
import { AppAction, AppContext } from "helpers/context";
import { Shape, DrawShapeSource } from "../../helpers/models";
import EditShapeDialog from "containers/my-shapes/edit-shape-dialog";
import { LeftPanel, RightPanel } from "helpers/constants";
import { addShape } from "api/my-shape";
import {
  areCoordinatesWithinShapeBounds,
  hasPositivePolygonRingArea
} from "helpers/shape-coordinate-validation";

function ShapeInfo() {
  const [t] = useTranslation();
  const { AMap, map } = useContext(MapContext);
  const [appContext, dispatch] = useContext(AppContext);
  const [formData, setFormData] = useState({
    drawName: "",
    projectType: ""
  });
  const [isEdit, setIsEdit] = useState(false);
  const [editBackupName, setEditBackupName] = useState("");
  const [openSaveDialog, setOpenSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveType, setSaveType] = useState<string>("Ground");
  const [savingShape, setSavingShape] = useState(false);
  const previousSelectedShapeIdRef = useRef<string | undefined>(undefined);

  // 根据当前选中 id 获取 shape 数据
  const selectedShape = appContext.shapes.find(
    (pl) => pl.id === appContext.selectedShapeId
  );

  const isPolygonSelected =
    selectedShape?.source === DrawShapeSource.POLYGON ||
    selectedShape?.source === DrawShapeSource.SHAPE_POLYGON;

  const showInvalidPolygonError = (title: string, message: string) => {
    dispatch({
      type: AppAction.addSnackBarParams,
      payload: {
        titleIcon: "error",
        titleIconColor: "red",
        title,
        message
      }
    });
  };

  const isPolygonSavable = () => {
    return hasPositivePolygonRingArea(selectedShape?.coordinates || []);
  };

  const handleOpenAnalyzeModal = () => {
    if (isPolygonSelected && !isPolygonSavable()) {
      showInvalidPolygonError(
        "invalidPolygonForAnalyzeTitle",
        "invalidPolygonForAnalyzeMessage"
      );
      return;
    }

    dispatch({ type: AppAction.setAnalyseShapeModal, payload: true });
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleCheckIndex = () => {
    dispatch({ type: AppAction.deselectShape });
  };

  const handleClose = () => {
    if (selectedShape?.aMapRef && map) {
      map.remove(selectedShape.aMapRef);
    }
    dispatch({
      type: AppAction.deleteShape,
      payload: selectedShape?.id
    });

    goBlack();
  };

  const goBlack = () => {
    // dispatch({ type: AppAction.setCurrentShape, payload: undefined });
    if (appContext.currentMultipleLocations) {
      dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Multiple });
    } else {
      dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Empty });
    }
  };

  const handleEditStart = () => {
    // 来源于 shape-list 的形状不允许改名
    if (selectedShape?.source === DrawShapeSource.SHAPE_LINE) {
      return;
    }
    setEditBackupName(formData.drawName);
    setIsEdit(true);
  };

  const handleEditConfirm = () => {
    if (selectedShape?.id) {
      const updatedShapes = appContext.shapes.map((shapeEntry) =>
        shapeEntry.id === selectedShape.id
          ? {
              ...shapeEntry,
              name: formData.drawName,
              type: formData.projectType
            }
          : shapeEntry
      );
      dispatch({
        type: AppAction.setShapes,
        payload: updatedShapes
      });
    }
    setIsEdit(false);
  };

  const handleEditCancel = () => {
    setFormData((prevData) => ({ ...prevData, drawName: editBackupName }));
    setIsEdit(false);
  };

  const handleDistance = () => {
    if (selectedShape?.aMapRef) {
      let path = selectedShape.aMapRef.getPath();
      if (Array.isArray(path?.[0])) {
        path = path[0];
      }

      if (isPolygonSelected) {
        const polygonPath = path.length > 2 ? [...path, path[0]] : path;
        const area = AMap?.GeometryUtil?.ringArea
          ? Math.abs(AMap.GeometryUtil.ringArea(polygonPath))
          : 0;

        if (area < 1000000) {
          return t("map.area(m2)", { data: area.toFixed(2) });
        }

        return t("map.area(km2)", { data: (area / 1000000).toFixed(3) });
      }

      let total = 0;
      for (let i = 0; i < path.length - 1; i++) {
        total += AMap.GeometryUtil.distance(path[i], path[i + 1]);
      }

      if (total < 1000) {
        return t("map.length(m)", { data: total.toFixed(3) });
      } else {
        return t("map.length(km)", { data: (total / 1000).toFixed(3) });
      }
    }
  };

  useEffect(() => {
    if (selectedShape) {
      setFormData({
        drawName: selectedShape.name,
        projectType: selectedShape.type || "Ground"
      });
    }

    if (selectedShape?.source === DrawShapeSource.SHAPE_LINE) {
      setIsEdit(false);
    }
  }, [selectedShape]);

  useEffect(() => {
    const currentShape = appContext.currentShape;
    const selectedId = selectedShape?.id;
    const selectedShapeRecordId = selectedShape?.shapeId;
    const previousSelectedShapeId = previousSelectedShapeIdRef.current;
    const isSelectedShapeChanged = selectedId !== previousSelectedShapeId;
    const isPolygonShape =
      selectedShape?.source === DrawShapeSource.POLYGON ||
      selectedShape?.source === DrawShapeSource.SHAPE_POLYGON;

    const targetShapePanel = isPolygonShape
      ? RightPanel.Polygon
      : RightPanel.Line;

    const isCurrentShapeAnalysisOwner = isPolygonShape
      ? !!selectedId &&
        (appContext.polygonAnalysisShapeId === selectedId ||
          (!!selectedShapeRecordId &&
            appContext.polygonAnalysisShapeId === selectedShapeRecordId))
      : !!selectedId &&
        (appContext.lineAnalysisShapeId === selectedId ||
          (!!selectedShapeRecordId &&
            appContext.lineAnalysisShapeId === selectedShapeRecordId));

    const isInShapePanel =
      appContext.rightPanel === RightPanel.Line ||
      appContext.rightPanel === RightPanel.Polygon;

    if (!selectedId) {
      if (isInShapePanel) {
        dispatch({
          type: AppAction.setRightPanel,
          payload: appContext.currentMultipleLocations
            ? RightPanel.Multiple
            : RightPanel.Empty
        });
      }
      previousSelectedShapeIdRef.current = selectedId;
      return;
    }

    const sameAsCurrentShape =
      !!currentShape &&
      (currentShape.id === selectedId ||
        currentShape.id === selectedShapeRecordId ||
        currentShape.shapeId === selectedId ||
        (selectedShapeRecordId &&
          currentShape.shapeId === selectedShapeRecordId));

    if (
      sameAsCurrentShape &&
      isCurrentShapeAnalysisOwner &&
      (isInShapePanel || isSelectedShapeChanged) &&
      appContext.rightPanel !== targetShapePanel
    ) {
      dispatch({ type: AppAction.setRightPanel, payload: targetShapePanel });
    } else if (!isCurrentShapeAnalysisOwner && isInShapePanel) {
      if (appContext.currentMultipleLocations) {
        dispatch({
          type: AppAction.setRightPanel,
          payload: RightPanel.Multiple
        });
      } else {
        dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Empty });
      }
    } else if (!sameAsCurrentShape && isInShapePanel) {
      if (appContext.currentMultipleLocations) {
        dispatch({
          type: AppAction.setRightPanel,
          payload: RightPanel.Multiple
        });
      } else {
        dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Empty });
      }
    }

    previousSelectedShapeIdRef.current = selectedId;
  }, [
    appContext.currentShape,
    appContext.rightPanel,
    appContext.currentMultipleLocations,
    appContext.lineAnalysisResult,
    appContext.lineAnalysisLegends,
    appContext.lineAnalysisStatus,
    appContext.polygonAnalysisResult,
    appContext.polygonAnalysisLegends,
    appContext.polygonLayerStatistics,
    appContext.polygonAnalysisStatus,
    selectedShape
  ]);

  const handleSaveShape = () => {
    if (isPolygonSelected && !isPolygonSavable()) {
      showInvalidPolygonError(
        "invalidPolygonForSaveTitle",
        "invalidPolygonForSaveMessage"
      );
      return;
    }

    if (!areCoordinatesWithinShapeBounds(selectedShape?.coordinates)) {
      showInvalidPolygonError(
        "invalidCoordinateBoundsTitle",
        "invalidCoordinateBoundsMessage"
      );
      return;
    }

    setSaveName(formData.drawName);
    setSaveType(formData.projectType || "Ground");
    setOpenSaveDialog(true);
  };

  const mapShapeTypeToProjectType = (shapeType: string) => {
    if (shapeType === "Aboveground") return "ABOVE" as const;
    if (shapeType === "Underground") return "UNDER" as const;
    return "GROUND" as const;
  };

  const MAX_SHAPE_NAME_LENGTH = 180;

  const handleConfirmSaveShape = async () => {
    if (!selectedShape || savingShape) return;

    const trimmedName = saveName.trim();
    if (!trimmedName || !selectedShape.coordinates?.length) {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          titleIcon: "error",
          titleIconColor: "red",
          title: "polygonHoleTitle",
          message: "shapeAddFailed"
        }
      });
      return;
    }

    if (trimmedName.length > MAX_SHAPE_NAME_LENGTH) {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          titleIcon: "error",
          titleIconColor: "red",
          title: "polygonHoleTitle",
          message: "shapeNameTooLongMessage"
        }
      });
      return;
    }

    if (isPolygonSelected && !isPolygonSavable()) {
      showInvalidPolygonError(
        "invalidPolygonForSaveTitle",
        "invalidPolygonForSaveMessage"
      );
      return;
    }

    if (!areCoordinatesWithinShapeBounds(selectedShape.coordinates)) {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          titleIcon: "error",
          titleIconColor: "red",
          title: "invalidCoordinateBoundsTitle",
          message: "invalidCoordinateBoundsMessage"
        }
      });
      return;
    }

    const geometryInfo = JSON.stringify({
      type: isPolygonSelected ? "Polygon" : "LineString",
      coordinates: isPolygonSelected
        ? [selectedShape.coordinates]
        : selectedShape.coordinates
    });

    const payload = {
      sourceId: "",
      sourceType: "INPUT" as const,
      geometryType: isPolygonSelected
        ? ("POLYGON" as const)
        : ("POLYLINE" as const),
      projectType: mapShapeTypeToProjectType(saveType),
      shapeName: trimmedName,
      geometryInfo,
      spatial: selectedShape.spatial || "WGS84"
    };

    try {
      setSavingShape(true);
      const result = await addShape(payload);
      const id: string | undefined = result?.data?.id;
      const createTime: number | undefined = result?.data?.createTime;
      if (!result || result.code !== 200 || !id) {
        const errorMessage = result?.message || result?.msg;
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: errorMessage
            ? {
                titleIcon: "error",
                titleIconColor: "red",
                title: "polygonHoleTitle",
                resMessage: errorMessage
              }
            : {
                titleIcon: "error",
                titleIconColor: "red",
                title: "polygonHoleTitle",
                message: "shapeAddFailed"
              }
        });
        return;
      }

      const tempFeature = pathToGeoJSON(
        selectedShape.coordinates,
        isPolygonSelected ? "Polygon" : "LineString"
      );
      const shape: Shape = {
        id,
        category: isPolygonSelected ? "polygon" : "polyline",
        name: trimmedName,
        type: saveType,
        feature: tempFeature,
        spatial: selectedShape.spatial,
        createTime
      };
      dispatch({ type: AppAction.addMyShapes, payload: shape });

      // 将手绘形状转换为 shape-list 来源后，保持样式一致
      if (selectedShape.aMapRef) {
        selectedShape.aMapRef.setOptions({
          strokeColor: "rgb(15, 77, 188)",
          strokeWeight: 4,
          strokeStyle: "solid",
          cursor: "pointer",
          draggable: false,
          bubble: true,
          geodesic: true,
          ...(isPolygonSelected && {
            fillColor: "rgb(15, 77, 188)",
            fillOpacity: 0.3
          })
        });
      }

      const updatedShapes = appContext.shapes.map((shapeEntry) =>
        shapeEntry.id === selectedShape.id
          ? {
              ...shapeEntry,
              source: isPolygonSelected
                ? DrawShapeSource.SHAPE_POLYGON
                : DrawShapeSource.SHAPE_LINE,
              name: trimmedName,
              type: saveType,
              shapeId: id,
              spatial: selectedShape.spatial,
              state: "selected" as any
            }
          : shapeEntry
      );

      dispatch({ type: AppAction.setShapes, payload: updatedShapes });
      dispatch({
        type: AppAction.selectShape,
        payload: selectedShape.id
      });

      if (appContext.leftPanel !== LeftPanel.MyShapes) {
        dispatch({ type: AppAction.setLeftPanel, payload: LeftPanel.MyShapes });
      }

      setOpenSaveDialog(false);
      setIsEdit(false);
    } catch {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          titleIcon: "error",
          titleIconColor: "red",
          title: "polygonHoleTitle",
          message: "shapeAddFailed"
        }
      });
    } finally {
      setSavingShape(false);
    }
  };

  const handleExportShape = () => {
    if (!selectedShape) {
      return;
    }

    let exportContent = "";

    if (
      (selectedShape.source === DrawShapeSource.SHAPE_LINE ||
        selectedShape.source === DrawShapeSource.SHAPE_POLYGON) &&
      selectedShape.shapeId
    ) {
      const shape = appContext.myShapes?.find(
        (s) => s.id === selectedShape.shapeId
      );
      if (shape?.feature) {
        exportContent = normalizeExportGeoJSON(shape.feature);
      }
    }

    if (!exportContent) {
      exportContent = pathToGeoJSON(
        selectedShape.coordinates,
        isPolygonSelected ? "Polygon" : "LineString"
      );
    }

    const blob = new Blob([exportContent], {
      type: "application/geo+json;charset=utf-8"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeName = (formData.drawName || "shape")
      .replace(/[\\/:*?"<>|]/g, "_")
      .trim();

    link.href = url;
    link.download = `${safeName || "shape"}.geojson`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const pathToGeoJSON = (
    coordinates: [number, number][] = [],
    geometryType: "LineString" | "Polygon" = "LineString",
    properties = {}
  ) => {
    let geometryCoordinates: any;

    if (geometryType === "Polygon") {
      // 去除连续重复点（AMap MouseTool 双击完成时会产生末尾重复）
      const deduped = coordinates.filter(
        (pt, i) =>
          i === 0 ||
          pt[0] !== coordinates[i - 1][0] ||
          pt[1] !== coordinates[i - 1][1]
      );

      // GeoJSON 规范要求多边形环首尾相同（闭合）
      const ring =
        deduped.length >= 2 &&
        (deduped[0][0] !== deduped[deduped.length - 1][0] ||
          deduped[0][1] !== deduped[deduped.length - 1][1])
          ? [...deduped, deduped[0]]
          : deduped;

      geometryCoordinates = [ring];
    } else {
      geometryCoordinates = coordinates;
    }

    return JSON.stringify({
      type: "Feature",
      geometry: {
        type: geometryType,
        coordinates: geometryCoordinates
      },
      properties
    });
  };

  const normalizeExportGeoJSON = (feature: unknown) => {
    let parsedFeature = feature;

    if (typeof parsedFeature === "string") {
      try {
        parsedFeature = JSON.parse(parsedFeature);
      } catch {
        return "";
      }
    }

    if (typeof parsedFeature === "string") {
      try {
        parsedFeature = JSON.parse(parsedFeature);
      } catch {
        return "";
      }
    }

    if (
      parsedFeature &&
      typeof parsedFeature === "object" &&
      (parsedFeature as any).type === "Feature" &&
      ((parsedFeature as any).geometry?.type === "LineString" ||
        (parsedFeature as any).geometry?.type === "Polygon") &&
      Array.isArray((parsedFeature as any).geometry?.coordinates)
    ) {
      return JSON.stringify({
        ...(parsedFeature as any),
        properties: (parsedFeature as any).properties || {}
      });
    }

    if (
      parsedFeature &&
      typeof parsedFeature === "object" &&
      (parsedFeature as any).type === "LineString" &&
      Array.isArray((parsedFeature as any).coordinates)
    ) {
      return pathToGeoJSON((parsedFeature as any).coordinates);
    }

    if (
      parsedFeature &&
      typeof parsedFeature === "object" &&
      (parsedFeature as any).type === "Polygon" &&
      Array.isArray((parsedFeature as any).coordinates?.[0])
    ) {
      return pathToGeoJSON((parsedFeature as any).coordinates[0], "Polygon");
    }

    return "";
  };

  if (!selectedShape) {
    return null;
  }

  const isShapeListSource =
    selectedShape.source === DrawShapeSource.SHAPE_LINE ||
    selectedShape.source === DrawShapeSource.SHAPE_POLYGON;

  return (
    <Paper className={styles.shapeInfo}>
      <div className={styles.shapeInfoHeader}>
        <div className={styles.shapeInfoTitle}>
          {isPolygonSelected ? (
            <PolygonSvg
              style={{
                flexShrink: 0,
                width: "20px",
                height: "20px",
                marginRight: "12px"
              }}
            />
          ) : (
            <LineSvg
              style={{
                flexShrink: 0,
                width: "20px",
                height: "20px",
                marginRight: "12px"
              }}
            />
          )}
          {isEdit ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: "10px"
              }}
            >
              <TextField
                size="small"
                color="success"
                sx={{
                  width: "230px",
                  marginRight: "5px"
                }}
                name="drawName"
                value={formData.drawName}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, drawName: e.target.value }))
                }
                disabled={isShapeListSource}
                inputProps={{ maxLength: MAX_SHAPE_NAME_LENGTH }}
              />
              <IconButton size="small" onClick={handleEditConfirm}>
                <span className="material-icons">check</span>
              </IconButton>
              <IconButton size="small" onClick={handleEditCancel}>
                <span className="material-icons">close</span>
              </IconButton>
            </div>
          ) : (
            <span
              style={{
                wordBreak: "break-all"
              }}
            >
              {formData.drawName}
            </span>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "row" }}>
          {!isShapeListSource && (
            <Tooltip title={t("drawShape.editTooltip")}>
              <IconButton size="small" onClick={handleEditStart}>
                <span className="material-icons">create</span>
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t("drawShape.minimizeTooltip")}>
            <IconButton size="small" onClick={handleCheckIndex}>
              <span className="material-icons">minimize</span>
            </IconButton>
          </Tooltip>
          <Tooltip title={t("drawShape.closeTooltip")}>
            <IconButton size="small" onClick={handleClose}>
              <span className="material-icons">close</span>
            </IconButton>
          </Tooltip>
        </div>
      </div>
      <div className={styles.shapeInfoContent}>
        <div className={styles.shapeInfoLabel}>
          <span>
            {isPolygonSelected ? t("drawShape.area") : t("drawShape.length")}:
          </span>
          <span>{handleDistance()}</span>
        </div>
        {!isPolygonSelected && (
          <div className={styles.shapeInfoProjectType}>
            <span>{t("drawShape.projectType")}:</span>
            {isShapeListSource ? (
              <span className="w-[145px] text-[12px] text-[#000000de]">
                {t(`myShapes.type-item.${formData.projectType || "Ground"}`)}
              </span>
            ) : (
              <Select
                name="projectType"
                value={formData.projectType}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    projectType: e.target.value
                  }))
                }
                color="success"
                size="small"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        fontSize: "12px"
                      }
                    }
                  }
                }}
                sx={{
                  width: "145px",
                  fontSize: "12px"
                }}
              >
                <MenuItem value="Ground">
                  {t("myShapes.type-item.Ground")}
                </MenuItem>
                <MenuItem value="Aboveground">
                  {t("myShapes.type-item.Aboveground")}
                </MenuItem>
                <MenuItem value="Underground">
                  {t("myShapes.type-item.Underground")}
                </MenuItem>
              </Select>
            )}
          </div>
        )}
      </div>
      <div className={styles.shapeInfoFooter}>
        {appContext.menu &&
          appContext.menu.find((item) => item.id === "105005") &&
          (!isShapeListSource ? (
            <Button
              variant="text"
              color="success"
              onClick={handleSaveShape}
              disabled={isEdit}
            >
              {t("buttons.saveToShapes")}
            </Button>
          ) : (
            <Button
              variant="text"
              color="success"
              onClick={handleExportShape}
              disabled={isEdit}
            >
              {t("buttons.exportGeoJson")}
            </Button>
          ))}
        <Button
          variant="text"
          color="success"
          onClick={handleOpenAnalyzeModal}
          disabled={isEdit}
          sx={{ marginLeft: "auto" }}
        >
          {t("buttons.Analyze")}
        </Button>
      </div>

      <EditShapeDialog
        open={openSaveDialog}
        onClose={() => setOpenSaveDialog(false)}
        onSave={handleConfirmSaveShape}
        editName={saveName}
        setEditName={setSaveName}
        category={isPolygonSelected ? "polygon" : "polyline"}
        type={saveType}
        setType={setSaveType}
        saveLoading={savingShape}
        saveDisabled={savingShape}
      />
    </Paper>
  );
}

export default ShapeInfo;
